// src/components/Products/WithBlaze.jsx
import { useLayoutEffect, useRef } from 'react';
import BlazeSlider from 'blaze-slider';

/**
 * Renders the Blaze slider markup (arrows + track) around `children` and
 * initializes Blaze on the nearest `.headless-loop-slider` ancestor, which is the
 * container the consumer renders with the `data-blaze` config.
 *
 * The slider element is resolved from a ref rather than a document-wide id
 * lookup, so every instance initializes its own element even when the same
 * markup exists more than once on the page.
 *
 * `refreshKey` should change whenever the slides change (e.g. a string of the
 * product ids) so Blaze is rebuilt against the new children.
 */
const WithBlaze = ({ refreshKey, children }) => {
    const wrapRef = useRef(null);

    useLayoutEffect(() => {
        const wrap = wrapRef.current;
        const sliderEl = wrap?.closest('.headless-loop-slider');
        const track = wrap?.querySelector('.blaze-track');

        if (!sliderEl || !track || track.children.length === 0) {
            return undefined;
        }

        let config = null;

        try {
            config = JSON.parse(sliderEl.dataset.blaze);
        } catch (error) {
            console.warn('WithBlaze: missing or invalid data-blaze config, slider not initialized.', error);
            return undefined;
        }

        const slider = new BlazeSlider(sliderEl, config);

        const prevButton = wrap.querySelector('.headless-slider-prev');
        const nextButton = wrap.querySelector('.headless-slider-next');
        const handlePrev = () => slider.prev();
        const handleNext = () => slider.next();

        prevButton?.addEventListener('click', handlePrev);
        nextButton?.addEventListener('click', handleNext);

        return () => {
            prevButton?.removeEventListener('click', handlePrev);
            nextButton?.removeEventListener('click', handleNext);
            slider.destroy();
            // Blaze never removes its window resize listener, so make sure a
            // later resize can't rebuild this instance on a detached element.
            slider.refresh = () => {};
        };
    }, [refreshKey]);

    return (
        <div className='headless-slider-wrap' ref={wrapRef}>
            <div className='headless-slider-prev' role='button' aria-label='Previous'>
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'>
                    <path d='M15 239c-9.4 9.4-9.4 24.6 0 33.9L207 465c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9L65.9 256 241 81c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0L15 239z' />
                </svg>
            </div>
            <div className='headless-slider-next' role='button' aria-label='Next'>
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'>
                    <path d='M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z' />
                </svg>
            </div>
            <div className='blaze-container'>
                <div className='blaze-track-container'>
                    <div className='blaze-track'>{children}</div>
                </div>
            </div>
        </div>
    );
};

export default WithBlaze;
